import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { duration, money, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export default function LiveCalls() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [stopOpen, setStopOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["live-calls"],
    queryFn: () => get("/calls/live"),
    refetchInterval: 2000,
  });

  const endCall = useMutation({
    mutationFn: (callId) => api.post(`/calls/${callId}/end`, {}),
    onSuccess: () => {
      toast.success("Call ended");
      queryClient.invalidateQueries({ queryKey: ["live-calls"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const pauseCampaign = useMutation({
    mutationFn: (campaignId) => api.post(`/campaigns/${campaignId}/pause`, { confirm: true }),
    onSuccess: () => {
      toast.success("Campaign paused");
      queryClient.invalidateQueries({ queryKey: ["live-calls"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const emergencyStop = async (reason) => {
    try {
      const { data: result } = await api.post("/emergency-stop", { reason, confirm: true });
      toast.success(`${result.campaigns_paused} campaigns paused · ${result.calls_stopped} calls stopped`);
      queryClient.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const calls = data?.calls || [];

  return (
    <>
      <PageHeader
        testId="live-calls-page"
        title="Live calls"
        subtitle="Real-time view of every call in flight, refreshed every 2 seconds"
        actions={
          can("live_calls", "write") ? (
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => setStopOpen(true)} data-testid="live-emergency-stop">
              Emergency stop all campaigns
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile testId="live-kpi-active" label="Active calls" value={data?.stats?.active ?? 0} tone="success" />
        <KpiTile testId="live-kpi-dialing" label="Dialing / ringing" value={data?.stats?.dialing ?? 0} tone="primary" />
        <KpiTile testId="live-kpi-queued" label="Queued" value={data?.stats?.queued ?? 0} />
        <KpiTile testId="live-kpi-campaigns" label="Running campaigns" value={data?.stats?.running_campaigns ?? 0} />
      </div>

      {calls.length === 0 ? (
        <Section title="No calls in flight" testId="live-empty">
          <p className="px-4 py-6 text-sm text-slate-500">
            Start a campaign from the Campaigns page to see live calls appear here within a few seconds.
          </p>
        </Section>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {calls.map((call) => (
            <Section
              key={call.id}
              testId={`live-call-${call.id}`}
              title={call.contact_name || call.phone}
              subtitle={call.mode === "human"
                ? `Human call · ${call.from_number || ""} → ${call.phone} · ${(call.country || "").toUpperCase()}`
                : `${call.campaign_name} · ${(call.language || "").toUpperCase()} · ${call.agent_name}`}
              actions={
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                    <span className="live-dot h-2 w-2 rounded-full bg-emerald-500" />
                    {duration(call.elapsed_seconds)}
                  </span>
                  <StatusBadge value={call.status} />
                </div>
              }
            >
              <div className="space-y-3 p-4">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="kpi-label">Live cost</p>
                    <p className="font-medium text-slate-800">{call.live_cost == null ? "—" : money(call.live_cost)}</p>
                  </div>
                  <div>
                    <p className="kpi-label">Connection</p>
                    <p className={cn("font-medium", call.connection_quality === "good" ? "text-emerald-600" : "text-amber-600")}>
                      {call.connection_quality ? title(call.connection_quality) : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="kpi-label">From</p>
                    <p className="font-mono text-xs text-slate-700">{call.from_number}</p>
                  </div>
                </div>

                <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2.5">
                  {call.mode === "human" ? (
                    <p className="text-xs text-slate-500">
                      Human call — you are speaking with the contact, so there is no AI transcript.
                    </p>
                  ) : (
                  <>
                  {call.transcript_source ? (
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400" data-testid={`live-transcript-source-${call.id}`}>
                      Source: {call.transcript_source}
                    </p>
                  ) : null}
                  {(call.live_transcript || []).length === 0 ? (
                    <p className="text-xs text-slate-500">Waiting for the provider transcript…</p>
                  ) : (
                    call.live_transcript.map((seg) => (
                      <p key={seg.id} className="text-xs">
                        <span className="font-mono text-slate-400">{duration(seg.offset_seconds)} </span>
                        <span className={seg.speaker === "customer" ? "text-slate-700" : "text-blue-700"}>
                          {seg.speaker === "customer" ? "Customer" : "AI"}:
                        </span>{" "}
                        {seg.text}
                      </p>
                    ))
                  )}
                  </>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/calls/${call.id}`)} data-testid={`live-open-${call.id}`}>
                    Open details
                  </Button>
                  {can("live_calls", "write") ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => endCall.mutate(call.id)} data-testid={`live-end-${call.id}`}>
                        End call
                      </Button>
                      {call.mode === "human" ? null : (
                        <Button size="sm" variant="outline" onClick={() => pauseCampaign.mutate(call.campaign_id)} data-testid={`live-pause-${call.id}`}>
                          Pause campaign
                        </Button>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            </Section>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={stopOpen}
        onOpenChange={setStopOpen}
        destructive
        requireReason
        reasonLabel="Reason for emergency stop"
        title="Stop all calling activity?"
        description="Queued calls are cancelled, active calls end safely, and every running campaign is paused. The user, reason, timestamp and affected calls are recorded."
        confirmLabel="Execute emergency stop"
        onConfirm={emergencyStop}
        testId="live-emergency-dialog"
      />
    </>
  );
}